/**
 * ログイン状態からどの画面を出すかの判定。
 *
 * これを純粋関数として切り出しているのは、ここを間違えると
 * **正規の利用者に「アクセス権がありません」を見せてしまう**ため。
 * 実際に一度やらかしている:
 *
 *   セッションは onAuthStateChange で先に確定するが、プロフィールは
 *   そのあと非同期で取りにいく。この「セッションはあるがプロフィールは未取得」
 *   の瞬間を denied と判定していたため、ログイン成功時に拒否画面が一瞬出ていた。
 *   外部の相手からは「ログインできない」と見える。
 *
 * 「まだ分からない」と「無い」を区別することがこの関数の役目。
 */
export type ViewState =
  /** セッションの有無すら分かっていない */
  | 'loading'
  /** 未ログイン */
  | 'login'
  /** ログイン済み。プロフィールを解決中（まだ権限の有無を判断できない） */
  | 'resolving'
  /** 解決した結果、このアカウントには権限が無い */
  | 'denied'
  /** 通常表示 */
  | 'app'

export type SessionViewInput = {
  /** 初回のセッション確認が終わったか */
  ready: boolean
  /** 現在のセッションの auth ユーザーID（未ログインなら null） */
  sessionUserId: string | null
  /** どの auth ユーザーについてプロフィール解決を終えたか */
  resolvedFor: string | null
  /** 解決の結果プロフィールがあったか */
  hasProfile: boolean
}

export function resolveView({
  ready,
  sessionUserId,
  resolvedFor,
  hasProfile,
}: SessionViewInput): ViewState {
  if (!ready) return 'loading'
  if (!sessionUserId) return 'login'

  // **ここが要点。** 解決が済んでいない間は denied と判定しない。
  // 解決対象が別のユーザーIDのままなら（ログインし直した直後など）まだ未解決。
  if (resolvedFor !== sessionUserId) return 'resolving'

  return hasProfile ? 'app' : 'denied'
}
